import { z } from "zod";
import {
  routingSchema,
  approvalRoutingNodeSchema,
  routingApprovalSchema,
} from "./routing-definition";
import {
  OverallStatus,
  Progress,
  type ProgressEventError,
  type ProgressEventPhase,
  ProgressStatus,
  ProgressType,
  type ReviewStep,
  ReviewerData,
  ReviewStatus,
  type EndStep,
} from "@/types/application";

type Routing = z.infer<typeof routingSchema>;
type ApprovalNode = z.infer<typeof approvalRoutingNodeSchema>;
type RoutingApproval = z.infer<typeof routingApprovalSchema>;
type RoutingNode = Routing["nodes"][number];
type RoutingNodeEvent = {
  eventId: string;
  name: string;
  status: string;
};
type EventContainer = {
  events?: Partial<Record<ProgressEventPhase, RoutingNodeEvent[]>>;
};

const nodeEventPhases = ["nodeEnter", "nodeLeave"] as const;
const taskEventPhases = ["taskEnter", "taskLeave"] as const;

function getApprovalRoleName(
  approval: RoutingApproval,
): { id: number; name: string } | undefined {
  const org = approval.assignee?.resolved_default_org;
  if (org?.type === "ROLE" && org.id && org.name) {
    return { id: org.id, name: org.name };
  }
  return undefined;
}

function inferRoleNameFromApprovals(
  approvals: RoutingApproval[],
): string | undefined {
  if (!approvals.length) return undefined;

  const resolvedRoles = approvals.map(getApprovalRoleName).filter(Boolean) as {
    id: number;
    name: string;
  }[];

  if (resolvedRoles.length !== approvals.length) return undefined;
  const first = resolvedRoles[0];
  return resolvedRoles.every((role) => role.id === first.id)
    ? first.name
    : undefined;
}

function toProgressStatus(
  status: "PENDING" | "INACTIVE" | "COMPLETED" | "FAILED" | "TBD" | "ERROR",
): ProgressStatus {
  switch (status) {
    case "PENDING":
      return ProgressStatus.Pending;
    case "INACTIVE":
      return ProgressStatus.NotStarted;
    case "COMPLETED":
      return ProgressStatus.Approved;
    case "FAILED":
      return ProgressStatus.Rejected;
    case "TBD":
      return ProgressStatus.Tbd;
    case "ERROR":
      return ProgressStatus.Rejected;
  }
  return ProgressStatus.NotStarted;
}

function toReviewStatus(
  status: "PENDING" | "WAITING" | "APPROVED" | "REJECTED" | "CANCELLED" | "TBD",
): ReviewStatus {
  switch (status) {
    case "PENDING":
      return ReviewStatus.Pending;
    case "WAITING":
      return ReviewStatus.NotStarted;
    case "APPROVED":
      return ReviewStatus.Approved;
    case "CANCELLED":
      return ReviewStatus.Canceled;
    case "REJECTED":
      return ReviewStatus.Rejected;
    case "TBD":
      return ReviewStatus.Tbd;
  }

  return ReviewStatus.Pending;
}

function mapReviewer(approval: RoutingApproval): ReviewerData {
  const assignee = approval.assignee;
  const isTbd = approval.status === "TBD" || assignee === null;
  const roleName = getApprovalRoleName(approval)?.name;
  const eventErrors = getEventErrors(
    approval,
    taskEventPhases,
    approval.approvalTaskId,
  );

  return {
    id: approval.approvalTaskId,
    type: "user",
    title: roleName,
    assigneeName: isTbd ? "Approver TBD" : (assignee?.name ?? "Approver TBD"),
    status: toReviewStatus(approval.status),
    comment: undefined,
    timestamp: undefined,
    eventErrors: eventErrors.length > 0 ? eventErrors : undefined,
  };
}

type ApprovalProgressStep =
  | {
      id: string;
      title: string;
      status: ProgressStatus;
      errorMessage?: string;
      type: ProgressType.Review;
      children: ReviewerData[];
      isReportingLine: boolean;
      requiresAllApprovals: boolean;
    }
  | {
      id: string;
      title: string;
      status: ProgressStatus;
      errorMessage?: string;
      type: ProgressType.Group;
      method: "AND" | "OR";
      children: {
        title?: string;
        method: "AND" | "OR";
        data: ReviewerData[];
        isReportingLine: boolean;
        eventErrors?: ProgressEventError[];
      }[];
    };

function mapApprovalNode(
  node: ApprovalNode,
  overallStatus?: OverallStatus,
): ApprovalProgressStep {
  const eventErrors = getEventErrors(node, nodeEventPhases, node.key);
  const base = {
    id: node.key,
    title: node.desc || node.type,
    status: toProgressStatus(node.status),
    errorMessage: node.errorMessage ?? node.error_message,
    eventErrors: eventErrors.length > 0 ? eventErrors : undefined,
  };

  const groups =
    node.approvalGroups?.flatMap((group, index) => {
      const subgroupMap = new Map<number, RoutingApproval[]>();
      group.approvals.forEach((approval) => {
        const subgroupIndex =
          approval.subGroupIndex ?? approval.sub_group_index ?? 0;
        const subgroup = subgroupMap.get(subgroupIndex) ?? [];
        subgroup.push(approval);
        subgroupMap.set(subgroupIndex, subgroup);
      });
      const subgroups = Array.from(subgroupMap.entries()).sort(
        (a, b) => a[0] - b[0],
      );
      return subgroups.map(([subgroupIndex, approvals], subgroupOrder) => {
        const inferredRoleName = inferRoleNameFromApprovals(approvals);
        const groupEventErrors = getEventErrors(
          group,
          nodeEventPhases,
          `${node.key}-group-${subgroupIndex}`,
        );
        return {
          title: group.desc
            ? subgroups.length > 1
              ? `${group.desc} (${subgroupOrder + 1})`
              : group.desc
            : inferredRoleName
              ? inferredRoleName
              : group.isReportingLine
                ? "Reporting line"
                : node.approvalGroups && node.approvalGroups.length > 1
                  ? `Group ${index + 1}${subgroups.length > 1 ? `.${subgroupIndex + 1}` : ""}`
                  : subgroups.length > 1
                    ? `Group ${subgroupIndex + 1}`
                    : undefined,
          method: group.requiresAllApprovals
            ? ("AND" as const)
            : ("OR" as const),
          data: approvals.map((approval) => mapReviewer(approval)),
          isReportingLine: group.isReportingLine,
          eventErrors:
            groupEventErrors.length > 0 ? groupEventErrors : undefined,
        };
      });
    }) ?? [];

  if (
    (node.status === "TBD" || node.status === "ERROR") &&
    groups.length === 0
  ) {
    return {
      ...base,
      type: ProgressType.Review as const,
      status: toProgressStatus(node.status),
      children: [],
      isReportingLine: false,
      requiresAllApprovals: false,
    };
  }

  if (node.approvalMethod === "parallel" || groups.length > 1) {
    const step = {
      ...base,
      type: ProgressType.Group as const,
      method: node.approvalLogic ?? "AND",
      children: groups,
    };
    return {
      ...step,
      status: deriveGroupStatus(step.children, base.status, overallStatus),
    };
  }

  const isReportingLine = groups[0].isReportingLine;
  const requiresAllApprovals = groups[0].method === "AND";
  const reviewers = groups.flatMap((group) => group.data);
  return {
    ...base,
    type: ProgressType.Review as const,
    status: deriveReviewStatus(reviewers, base.status, overallStatus),
    children: reviewers,
    isReportingLine,
    requiresAllApprovals,
  };
}

function isFailedEvent(status: string): boolean {
  const upperStatus = status.toUpperCase();
  return upperStatus === "FAILED" || upperStatus === "ERROR";
}

function mapEventError(
  ownerId: string,
  event: RoutingNodeEvent,
  phase: ProgressEventPhase,
): ProgressEventError {
  return {
    id: `${ownerId}-${phase}-${event.eventId}`,
    eventId: event.eventId,
    name: event.name,
    status: event.status,
    phase,
  };
}

function getEventErrorsByPhase(
  container: EventContainer,
  phase: ProgressEventPhase,
  ownerId: string,
): ProgressEventError[] {
  return (
    container.events?.[phase]
      ?.filter((event) => isFailedEvent(event.status))
      .map((event) => mapEventError(ownerId, event, phase)) ?? []
  );
}

function getEventErrors(
  container: EventContainer,
  phases: readonly ProgressEventPhase[],
  ownerId = "event",
): ProgressEventError[] {
  return phases.flatMap((phase) =>
    getEventErrorsByPhase(container, phase, ownerId),
  );
}

function hasEventErrors(
  container: EventContainer,
  phases: readonly ProgressEventPhase[],
): boolean {
  return phases.some(
    (phase) =>
      container.events?.[phase]?.some((event) => isFailedEvent(event.status)) ??
      false,
  );
}

function mapNonApprovalNode(node: RoutingNode): ReviewStep {
  const eventErrors = getEventErrors(node, nodeEventPhases, node.key);
  return {
    id: node.key,
    title: node.desc || node.type,
    type: ProgressType.Review,
    status:
      eventErrors.length > 0
        ? ProgressStatus.Rejected
        : toProgressStatus(node.status),
    errorMessage: node.errorMessage ?? node.error_message,
    eventErrors: eventErrors.length > 0 ? eventErrors : undefined,
    children: [],
    isReportingLine: false,
    requiresAllApprovals: false,
  };
}

function shouldShowApprovalNode(node: ApprovalNode): boolean {
  return (
    node.status === "TBD" ||
    node.status === "ERROR" ||
    hasEventErrors(node, nodeEventPhases) ||
    node.approvalGroups?.some((group) => group.approvals.length > 0)
  );
}

function isActiveConditionChild(node: RoutingNode): boolean {
  if (node.status !== "INACTIVE") {
    return true;
  }

  if (hasEventErrors(node, nodeEventPhases)) {
    return true;
  }

  if (node.type !== "approval") {
    return false;
  }

  return node.approvalGroups.some(
    (group) =>
      hasEventErrors(group, nodeEventPhases) ||
      group.approvals.some(
        (approval) =>
          approval.status !== "WAITING" ||
          hasEventErrors(approval, taskEventPhases),
      ),
  );
}

function getConditionChildNodes(
  node: RoutingNode,
  nodeByKey: Map<string, RoutingNode>,
): RoutingNode[] {
  const childNodes = (node.child_keys ?? [])
    .map((childKey) => nodeByKey.get(childKey))
    .filter((childNode): childNode is RoutingNode => Boolean(childNode));
  const activeChildNodes = childNodes.filter(isActiveConditionChild);

  return activeChildNodes.length > 0 ? activeChildNodes : childNodes;
}

function dedupeEventErrors(
  events: ProgressEventError[] | undefined,
  ownerKey: string,
  seenEventErrors: Set<string>,
): ProgressEventError[] | undefined {
  const deduped = events?.filter((event) => {
    const key = `${ownerKey}:${event.phase}:${event.eventId}:${event.status}`;
    if (seenEventErrors.has(key)) {
      return false;
    }
    seenEventErrors.add(key);
    return true;
  });

  return deduped?.length ? deduped : undefined;
}

function dedupeProgressEventErrors(
  progress: Progress,
  seenEventErrors = new Set<string>(),
): Progress {
  progress.forEach((step) => {
    step.eventErrors = dedupeEventErrors(
      step.eventErrors,
      `${step.type}:${step.title}`,
      seenEventErrors,
    );

    if (step.type === ProgressType.Condition) {
      step.children.forEach((branch) =>
        dedupeProgressEventErrors(branch, seenEventErrors),
      );
      return;
    }

    if (step.type === ProgressType.Group) {
      step.children.forEach((group) => {
        group.eventErrors = dedupeEventErrors(
          group.eventErrors,
          `${step.type}:${step.title}:${group.title ?? ""}`,
          seenEventErrors,
        );
        group.data.forEach((reviewer) => {
          reviewer.eventErrors = dedupeEventErrors(
            reviewer.eventErrors,
            `reviewer:${reviewer.title ?? ""}:${reviewer.assigneeName}`,
            seenEventErrors,
          );
        });
      });
      return;
    }

    if (step.type === ProgressType.Review) {
      step.children.forEach((reviewer) => {
        reviewer.eventErrors = dedupeEventErrors(
          reviewer.eventErrors,
          `reviewer:${reviewer.title ?? ""}:${reviewer.assigneeName}`,
          seenEventErrors,
        );
      });
    }
  });

  return progress;
}

function deriveReviewStatus(
  reviewers: ReviewerData[],
  fallback: ProgressStatus,
  overallStatus?: OverallStatus,
): ProgressStatus {
  if (reviewers.some((reviewer) => reviewer.status === ReviewStatus.Rejected)) {
    return ProgressStatus.Rejected;
  }
  const allWaiting =
    reviewers.length > 0 &&
    reviewers.every((reviewer) => reviewer.status === ReviewStatus.NotStarted);
  if (
    allWaiting &&
    fallback === ProgressStatus.NotStarted &&
    overallStatus === OverallStatus.CompletedRejected
  ) {
    return ProgressStatus.Canceled;
  }
  return fallback;
}

function deriveGroupStatus(
  groups: { data: ReviewerData[] }[],
  fallback: ProgressStatus,
  overallStatus?: OverallStatus,
): ProgressStatus {
  const hasRejected = groups.some((group) =>
    group.data.some((reviewer) => reviewer.status === ReviewStatus.Rejected),
  );
  if (hasRejected) {
    return ProgressStatus.Rejected;
  }
  const allWaiting =
    groups.length > 0 &&
    groups.every(
      (group) =>
        group.data.length > 0 &&
        group.data.every(
          (reviewer) => reviewer.status === ReviewStatus.NotStarted,
        ),
    );
  if (
    allWaiting &&
    fallback === ProgressStatus.NotStarted &&
    overallStatus === OverallStatus.CompletedRejected
  ) {
    return ProgressStatus.Canceled;
  }
  return fallback;
}

function deriveConditionStatus(
  childSteps: Progress,
  fallback: ProgressStatus,
): ProgressStatus {
  const statuses = childSteps.map((step) => step.status);

  const allCanceled =
    statuses.length > 0 &&
    statuses.every((status) => status === ProgressStatus.Canceled);
  if (allCanceled) {
    return ProgressStatus.Canceled;
  }

  const allRejected =
    statuses.length > 0 &&
    statuses.every((status) => status === ProgressStatus.Rejected);
  if (allRejected) {
    return ProgressStatus.Rejected;
  }

  if (statuses.some((status) => status === ProgressStatus.Rejected)) {
    return ProgressStatus.Rejected;
  }

  if (statuses.some((status) => status === ProgressStatus.Pending)) {
    return ProgressStatus.Pending;
  }

  if (statuses.every((status) => status === ProgressStatus.Approved)) {
    return ProgressStatus.Approved;
  }

  if (statuses.every((status) => status === ProgressStatus.NotStarted)) {
    return ProgressStatus.NotStarted;
  }

  return fallback;
}

function getCompletionStatus(overallStatus?: OverallStatus): ProgressStatus {
  switch (overallStatus) {
    case OverallStatus.CompletedApproved:
      return ProgressStatus.Approved;
    case OverallStatus.CompletedRejected:
      return ProgressStatus.Rejected;
    default:
      return ProgressStatus.NotStarted;
  }
}

function getOrderedRoutingNodes(routing: Routing): RoutingNode[] {
  const nodesByKey = new Map(routing.nodes.map((node) => [node.key, node]));
  const visited = new Set<string>();
  const ordered: RoutingNode[] = [];

  const visit = (key: string) => {
    if (visited.has(key)) return;

    const node = nodesByKey.get(key);
    if (!node) return;

    visited.add(key);
    ordered.push(node);

    node.child_keys?.forEach(visit);
  };

  routing.nodes
    .filter((node) => node.parent_keys.length === 0)
    .forEach((node) => visit(node.key));

  routing.nodes.forEach((node) => visit(node.key));

  return ordered;
}

export function tApplicationProgress(
  routing?: Routing,
  overallStatus?: OverallStatus,
): Progress {
  if (!routing?.nodes?.length) return [];

  const nodeByKey = new Map(routing.nodes.map((node) => [node.key, node]));
  const orderedNodes = getOrderedRoutingNodes(routing);
  const conditionChildKeys = new Set(
    routing.nodes.flatMap((node) =>
      node.type === "condition" ? (node.child_keys ?? []) : [],
    ),
  );
  const steps: Progress = [];

  orderedNodes.forEach((node) => {
    if (node.type === "condition") {
      const eventErrors = getEventErrors(node, nodeEventPhases, node.key);
      const childBranches: Progress[] = [];
      getConditionChildNodes(node, nodeByKey).forEach((childNode) => {
        const branchSteps: Progress = [];

        if (
          childNode.type === "approval" &&
          shouldShowApprovalNode(childNode)
        ) {
          branchSteps.push(mapApprovalNode(childNode, overallStatus));
        }

        if (branchSteps.length > 0) {
          childBranches.push(branchSteps);
        }
      });

      if (childBranches.length > 0 || eventErrors.length > 0) {
        steps.push({
          id: node.key,
          title: node.desc || "Condition",
          type: ProgressType.Condition,
          status:
            eventErrors.length > 0
              ? ProgressStatus.Rejected
              : deriveConditionStatus(
                  childBranches.flat(),
                  toProgressStatus(node.status),
                ),
          eventErrors: eventErrors.length > 0 ? eventErrors : undefined,
          children: childBranches,
        });
      }
      return;
    }

    if (node.type === "approval") {
      if (!conditionChildKeys.has(node.key) && shouldShowApprovalNode(node)) {
        steps.push(mapApprovalNode(node, overallStatus));
      }
      return;
    }

    const eventErrors = getEventErrors(node, nodeEventPhases, node.key);
    if (node.status === "ERROR" || eventErrors.length > 0) {
      steps.push(mapNonApprovalNode(node));
    }
  });

  const completedStep: EndStep = {
    id:
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `completed-${Date.now()}`,
    title: "Completed",
    type: ProgressType.End as const,
    status: getCompletionStatus(overallStatus),
    children: null,
  };

  return dedupeProgressEventErrors([...steps, completedStep]);
}
